import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import {
  Button,
  DateRangePicker,
  H1,
  InfoBlock,
  Table,
} from '@smartshell/ui-kit';
import dayjs from 'dayjs';

import { useSharedDateRange, useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import {
  ETabs,
  GET_BONUS_TIME_SERIES_REPORT,
  GET_DETAILED_SUMMARY_REPORT,
  GET_DETAILED_SUMMARY_REPORT_FILE,
  GET_USER_BONUS_REPORT,
  GET_USER_BONUS_REPORT_FILE,
  IBonusDetailedSummaryReportFileResponse,
  IBonusDetailedSummaryReportRequest,
  IBonusDetailedSummaryReportResponse,
  IBonusDetailedTimeSeriesReportRequest,
  IBonusDetailedTimeSeriesReportResponse,
  IBonusUsersBonusReportFileResponse,
  IBonusUsersBonusReportRequest,
  IBonusUsersBonusReportResponse,
} from 'schemas';
import { formatDateForServer } from 'utils';

import s from './Bonus.module.scss';
import { BonusSummary, DynamicBonus, SourceBonus } from './components';
import { IBonus } from './interfaces';
import { useColumns } from './useColumns';

export const Bonus: FC<IBonus> = () => {
  const { t, lang } = useContext(LocalizationContext);
  const today = dayjs();
  const {
    startDate,
    endDate,
    periodType,
    offset,
    setStartDate,
    setEndDate,
    setPeriodType,
    setOffset,
  } = useSharedDateRange();
  const [slice, setSlice] = useState<ETabs>(ETabs.DAY);
  const [error, setError] = useState<string | null>(null);

  const [
    getDetailedSummaryBonus,
    {
      data: summaruBonus,
      loading: summaryBonusLoading,
      variables: summaryBonusVariables,
    },
  ] = useLazyQuery<
    IBonusDetailedSummaryReportResponse,
    IBonusDetailedSummaryReportRequest
  >(GET_DETAILED_SUMMARY_REPORT, {
    fetchPolicy: 'no-cache',
    onError: (error) => {
      setError(error.message);
    },
  });

  const [getDetailedSummaryBonusFile, { loading: summaryBonusFileLoading }] =
    useLazyQuery<
      IBonusDetailedSummaryReportFileResponse,
      IBonusDetailedSummaryReportRequest
    >(GET_DETAILED_SUMMARY_REPORT_FILE, {
      fetchPolicy: 'no-cache',
      onCompleted: (response) => {
        if (!response) return;

        open(response?.bonusDetailedSummaryReport?.url, '_self');
      },
    });

  const [
    getBonusSeriesReport,
    { data: bonusSeries, variables, loading: bonusSeriesLoading },
  ] = useLazyQuery<
    IBonusDetailedTimeSeriesReportResponse,
    IBonusDetailedTimeSeriesReportRequest
  >(GET_BONUS_TIME_SERIES_REPORT, {
    fetchPolicy: 'no-cache',
    onError: (error) => {
      setError(error.message);
    },
  });

  const [
    getUsersBonusReport,
    { data: usersBonus, loading: usersBonusReportLoading },
  ] = useLazyQuery<
    IBonusUsersBonusReportResponse,
    IBonusUsersBonusReportRequest
  >(GET_USER_BONUS_REPORT, {
    fetchPolicy: 'cache-and-network',
    onError: (error) => {
      setError(error.message);
    },
  });
  const [getUsersBonusReportFile, { loading: usersBonusReportFileLoading }] =
    useLazyQuery<
      IBonusUsersBonusReportFileResponse,
      IBonusUsersBonusReportRequest
    >(GET_USER_BONUS_REPORT_FILE, {
      fetchPolicy: 'no-cache',
      onCompleted: (response) => {
        if (!response) return;

        open(response?.usersBonusReport?.url, '_self');
      },
    });

  const handleClick = (): void => {
    getDetailedSummaryBonus({
      variables: {
        input: {
          start: formatDateForServer(dayjs(startDate)),
          end: formatDateForServer(dayjs(endDate)),
        },
      },
    });

    getBonusSeriesReport({
      variables: {
        input: {
          from: formatDateForServer(dayjs(startDate)),
          to: formatDateForServer(dayjs(endDate)),
          groupBy: slice,
        },
      },
    });

    getUsersBonusReport({
      variables: {
        input: {
          from: formatDateForServer(dayjs(startDate)),
          to: formatDateForServer(dayjs(endDate)),
        },
      },
    });
  };

  useEffect(() => {
    if (variables?.input) {
      getBonusSeriesReport({
        variables: { input: { ...variables.input, groupBy: slice } },
      });
    }
  }, [slice]);

  const normalizedData = useMemo(() => {
    if (!usersBonus?.usersBonusReport) return [];

    const { extraLabels, extraLabels2, extraLabels3, keys, data } =
      usersBonus?.usersBonusReport;

    return data.map((item: any, index: number) => {
      const values = item.values;

      const phone = extraLabels[index] || '';
      const nickname = keys[index] || '';
      const avatar_url = extraLabels2[index] || '';
      const isWorker =
        (extraLabels3[index] || '').trim().toLowerCase() === 'worker';

      return {
        client: {
          nickname,
          phone,
          isWorker,
          avatar_url,
        },
        renewal: values[0] || 0,
        cashback: values[1] || 0,
        achievements: values[2] || 0,
        promocode: values[3] || 0,
        accrued: values[4] || 0,
        waste: values[5] || 0,
      };
    });
  }, [usersBonus]);

  const COLUMNS = useColumns();
  const size = useWindowResize();

  return (
    <div className={s.container}>
      {error && (
        <InfoBlock title={t('errors.error')} message={error} direction="row" />
      )}
      <div className={s.controls}>
        <DateRangePicker
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          lastValidDate={today.format()}
          firstValidDate={dayjs('2022-03-01T00:00:00').format()}
          lang={lang}
          periodType={periodType}
          setPeriodType={setPeriodType}
          offset={offset}
          setOffset={setOffset}
        />

        <div className={s.right}>
          <Button
            onClick={() => {
              handleClick();
            }}
            disabled={summaryBonusLoading}
          >
            {t('reports.generate')}
          </Button>
          <Button
            className={s.export}
            iconName="IncomeIcon"
            variant="outline"
            onClick={async () => {
              if (summaryBonusVariables) {
                await getDetailedSummaryBonusFile({
                  variables: summaryBonusVariables,
                });

                await getUsersBonusReportFile({
                  variables: {
                    input: {
                      from: formatDateForServer(dayjs(startDate)),
                      to: formatDateForServer(dayjs(endDate)),
                    },
                  },
                });
              }
            }}
            isLoading={summaryBonusFileLoading || usersBonusReportFileLoading}
            disabled={
              !variables?.input ||
              summaryBonusFileLoading ||
              usersBonusReportFileLoading
            }
          >
            {size.width < 1864 && size.width > 960 ? t('csv') : t('export.csv')}
          </Button>
        </div>
      </div>

      <div className={s.content}>
        <div className={s.block}>
          <H1>{t('report.sales.title')}</H1>
          <BonusSummary
            data={summaruBonus?.bonusDetailedSummaryReport.summary}
            loading={summaryBonusLoading}
          />
        </div>

        <div className={s.chart_block}>
          <div className={s.top}>
            <DynamicBonus
              slice={slice}
              setSlice={setSlice}
              bonusSeriesLoading={bonusSeriesLoading}
              bonusSeries={bonusSeries}
            />
            <SourceBonus report={summaruBonus} loading={summaryBonusLoading} />
          </div>
          <Table
            data={normalizedData}
            columns={COLUMNS}
            emptyDataPlaceholder={
              usersBonus?.usersBonusReport
                ? t('visitors.empty.data')
                : t('report.placeholder.emptyDates')
            }
            className={s.table}
            isLoading={usersBonusReportLoading}
          />
        </div>
      </div>
    </div>
  );
};
